if (!customElements.get('standard-product')) {
  class StandardProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.select = this.querySelector('[data-main-select]');
      this.button = this.querySelector('[data-action="add-to-cart"]');
      this.inputs = Array.from(this.querySelectorAll('[data-option-input]'));
      this.groups = Array.from(this.querySelectorAll('[data-option-group]'));
      this.priceEls = Array.from(this.querySelectorAll('[data-product-price]'));
      this.compareEls = Array.from(this.querySelectorAll('[data-product-compare]'));

      const variantsEl = this.querySelector('[data-variants]');
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
      const plansEl = this.querySelector('[data-selling-plans]');
      this.sellingPlans = plansEl ? JSON.parse(plansEl.textContent) : {};

      this.optionCount = this.inputs.reduce((max, input) => {
        return Math.max(max, parseInt(input.dataset.optionPosition, 10) || 0);
      }, 0);

      this.inputs.forEach(input => {
        input.addEventListener('change', () => this.update());
      });

      this.update();
    }

    // Positional array of selected option values; null where nothing is chosen yet.
    getSelectedOptions() {
      const selected = new Array(this.optionCount).fill(null);

      this.inputs.forEach(input => {
        if (!input.checked) return;
        const index = (parseInt(input.dataset.optionPosition, 10) || 1) - 1;
        selected[index] = input.value;
      });

      return selected;
    }

    getVariant(selected) {
      if (!this.optionCount) {
        return this.variants.find(v => String(v.id) === String(this.select && this.select.value));
      }

      if (selected.some(value => value === null)) return null;

      return this.variants.find(variant => {
        return selected.every((value, index) => variant.options[index] === value);
      });
    }

    update() {
      const selected = this.getSelectedOptions();
      const variant = this.getVariant(selected);

      this.updateAvailability(selected);
      this.updateGroupLabels();

      if (variant) {
        const allocation = this.updateSellingPlan(variant);
        this.syncForm(variant);
        this.updatePrice(variant, allocation);
      }

      this.updateButtonState(variant, selected);
    }

    syncForm(variant) {
      if (this.select && String(this.select.value) !== String(variant.id)) {
        this.select.value = variant.id;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));
    }

    // Attaches the variant's preselected selling plan to the form, so a subscription
    // product adds to cart on its plan rather than as a one-time purchase.
    updateSellingPlan(variant) {
      const allocation = this.sellingPlans[variant.id] || null;
      if (!this.sellingPlanInput) return allocation;

      if (allocation && allocation.id) {
        this.sellingPlanInput.value = allocation.id;
        this.sellingPlanInput.disabled = false;
      } else {
        this.sellingPlanInput.value = '';
        this.sellingPlanInput.disabled = true;
      }

      return allocation;
    }

    updatePrice(variant, allocation) {
      const usePlan = allocation && allocation.price != null;
      const amount = usePlan ? allocation.price : variant.price;
      const compareAmount = usePlan ? allocation.compare_at_price : variant.compare_at_price;

      const price = this.formatMoney(amount);
      this.priceEls.forEach(el => {
        el.textContent = price;
      });

      const hasDiscount = compareAmount && compareAmount > amount;
      this.compareEls.forEach(el => {
        el.textContent = hasDiscount ? this.formatMoney(compareAmount) : '';
        el.hidden = !hasDiscount;
      });
    }

    // Grey out values that cannot form an available variant with the other current selections.
    updateAvailability(selected) {
      if (!this.optionCount) return;

      this.inputs.forEach(input => {
        const index = (parseInt(input.dataset.optionPosition, 10) || 1) - 1;

        const available = this.variants.some(variant => {
          if (!variant.available) return false;
          if (variant.options[index] !== input.value) return false;

          return selected.every((value, i) => {
            if (i === index || value === null) return true;
            return variant.options[i] === value;
          });
        });

        input.disabled = !available;
      });
    }

    // Each group's label shows the selected value only when the selection lives in that group.
    updateGroupLabels() {
      this.groups.forEach(group => {
        const target = group.querySelector('[data-group-selected]');
        if (!target) return;

        const checked = group.querySelector('[data-option-input]:checked');
        target.textContent = checked ? checked.value : '';
      });
    }

    updateButtonState(variant, selected) {
      if (!this.button) return;

      const incomplete = this.optionCount > 0 && selected.some(value => value === null);

      if (incomplete) {
        this.button.disabled = true;
        this.button.textContent = 'Select a size';
        return;
      }

      const available = !!(variant && variant.available);
      this.button.disabled = !available;
      this.button.textContent = available ? 'Add to cart' : 'Sold out';
    }

    formatMoney(cents) {
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }

      const dollars = cents / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(dollars);
    }
  }

  customElements.define('standard-product', StandardProduct);
}
